import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { BarChart3, Loader2, X } from "lucide-react";
import { VariavelWiredSelect } from "../../VariavelWiredSelect";
import { WiredConexao, WiredEstatistica, wiredService } from "../../../services/wired";

interface WiredEstatisticaModalProps {
  isOpen: boolean;
  onClose: () => void;
  estatistica: WiredEstatistica | null;
  conexoes: WiredConexao[];
  categorias: string[];
  onSuccess?: () => void;
}

const NOME_VARIAVEL_REGEX = /^[\w.-]{1,128}$/;

export function WiredEstatisticaModal({
  isOpen,
  onClose,
  estatistica,
  conexoes,
  categorias,
  onSuccess,
}: WiredEstatisticaModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [idConexao, setIdConexao] = useState("");
  const [nomeVariavel, setNomeVariavel] = useState("");
  const [rotulo, setRotulo] = useState("");
  const [categoria, setCategoria] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setIdConexao(estatistica?.id_conexao ?? conexoes[0]?.id ?? "");
    setNomeVariavel(estatistica?.nome_variavel ?? "");
    setRotulo(estatistica?.rotulo ?? "");
    setCategoria(estatistica?.categoria ?? "");
    setAtivo(estatistica?.ativo ?? true);
    setError("");
  }, [isOpen, estatistica, conexoes]);

  const validar = (): string => {
    if (!idConexao) return "Selecione uma conexão.";
    if (!NOME_VARIAVEL_REGEX.test(nomeVariavel.trim())) return "Selecione a variável.";
    if (!rotulo.trim() || rotulo.trim().length > 80) return "Nome do campo é obrigatório (máximo 80 caracteres).";
    if (!categoria.trim() || categoria.trim().length > 60) return "Categoria é obrigatória (máximo 60 caracteres).";
    return "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const mensagem = validar();
    if (mensagem) {
      setError(mensagem);
      return;
    }

    const payload = {
      id_conexao: idConexao,
      nome_variavel: nomeVariavel.trim(),
      rotulo: rotulo.trim(),
      categoria: categoria.trim(),
      ativo,
    };

    try {
      setSubmitting(true);
      if (estatistica) {
        await wiredService.atualizarEstatistica(estatistica.id, payload);
        enqueueSnackbar("Estatística atualizada com sucesso!", { variant: "success" });
      } else {
        await wiredService.criarEstatistica(payload);
        enqueueSnackbar("Estatística criada com sucesso!", { variant: "success" });
      }
      onClose();
      if (onSuccess) onSuccess();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || "Erro ao salvar estatística", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              {estatistica ? "Editar estatística" : "Nova estatística"}
            </CardTitle>
            <CardDescription>Contador exibido na aba Estatísticas do Sobrevivente</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0" disabled={submitting}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Conexão</Label>
              <Select value={idConexao} onValueChange={setIdConexao} disabled={submitting}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma conexão" />
                </SelectTrigger>
                <SelectContent>
                  {conexoes.map((conexao) => (
                    <SelectItem key={conexao.id} value={conexao.id}>
                      {conexao.nome} (quarto {conexao.room_id}){!conexao.ativo ? " - inativa" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="estatistica_variavel">Variável</Label>
              <VariavelWiredSelect
                id="estatistica_variavel"
                value={nomeVariavel}
                onChange={(v) => {
                  setNomeVariavel(v);
                  setError("");
                }}
                idConexao={idConexao}
                ativo={isOpen}
                placeholder="Buscar variável (ex: peixes_pescados)"
                disabled={submitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="estatistica_rotulo">Nome do campo</Label>
              <Input
                id="estatistica_rotulo"
                value={rotulo}
                onChange={(e) => {
                  setRotulo(e.target.value);
                  setError("");
                }}
                placeholder="Ex: Peixes pescados"
                disabled={submitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="estatistica_categoria">Categoria</Label>
              <Input
                id="estatistica_categoria"
                list="estatistica_categorias"
                value={categoria}
                onChange={(e) => {
                  setCategoria(e.target.value);
                  setError("");
                }}
                placeholder="Escolha uma existente ou digite para criar"
                disabled={submitting}
              />
              <datalist id="estatistica_categorias">
                {categorias.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <Label htmlFor="estatistica_ativo">Ativa</Label>
              <Switch id="estatistica_ativo" checked={ativo} onCheckedChange={setAtivo} disabled={submitting} />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={submitting}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : estatistica ? (
                  "Salvar"
                ) : (
                  "Criar"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
